import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useMeasure } from 'react-use';
import { Show } from '@components/Show';
import type { Action } from '@components/types';
import type { DragEndEvent } from '@dnd-kit/core';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { ActionsBar } from '@pages/User/components/ActionsBar';
import { FiltersBar } from '@pages/User/components/FiltersBar';
import type { ColumnFilter } from '@tanstack/react-table';
import clsx from 'clsx';
import { cn } from 'cn';

import { TableLoading } from '../../shared/components/TableLoading';
import { type UseDataTableReturn } from '../hooks/useDataTable';
import { TableEmptyState } from '../TableEmptyState';

import { TableHeader } from './table-header';
import { TableBody } from './TableBody';
import { TableFooter } from './TableFooter';

export interface TableContainerProps {
  actions: Action[];
  className?: string;
  enableColumnFilters: boolean;
  enableColumnVisibility: boolean;
  enableGlobalSearch: boolean;
  enablePagination: boolean;
  entityName: string;
  filtersBarWrapperClassName?: string;
  isEmpty?: boolean;
  isLoading?: boolean;
  pageSize: number;
  renderCustomElements?: (
    isGroupByParent: boolean,
    setIsGroupByParent: (isGroupByParent: boolean) => void,
  ) => () => ReactNode;
  tableApi: UseDataTableReturn;
  totalResults?: number;
  handleSearchClick?: (searchTerm: string) => void;
}

export function TableContainer({
  actions,
  className,
  enableColumnFilters,
  enableColumnVisibility,
  enableGlobalSearch,
  enablePagination,
  entityName,
  filtersBarWrapperClassName,
  isEmpty,
  isLoading = false,
  pageSize,
  renderCustomElements,
  tableApi,
  totalResults,
  handleSearchClick,
}: TableContainerProps) {
  const { table, tableState, handlers, meta } = tableApi;
  const getFeatureFlagState = useFeatureFlags();
  const useOpenSearch = getFeatureFlagState('OpenSearch') ?? false;

  const [isColumnVisibilityControlsVisible, setIsColumnVisibilityControlsVisible] = useState(false);
  const [isFiltersBarVisible, setIsFiltersBarVisible] = useState(false);
  const [hasFiltersBarMounted, setHasFiltersBarMounted] = useState(false);
  const [filterRef] = useMeasure<HTMLDivElement>();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const prevFilteredDataLengthRef = useRef<number>(undefined);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    const currentTotalResultsForDisplay = useOpenSearch ? meta.totalResultsForDisplay : meta.filteredRowsLength;
    if (prevFilteredDataLengthRef.current !== undefined) {
      if (currentTotalResultsForDisplay !== prevFilteredDataLengthRef.current && tableState?.pagination) {
        table.setPageIndex(0);
      }
    }
    prevFilteredDataLengthRef.current = currentTotalResultsForDisplay;
  }, [meta.filteredRowsLength, meta.totalResultsForDisplay, table, tableState?.pagination, useOpenSearch]);

  const toggleFiltersBarVisibility = () => {
    setHasFiltersBarMounted(true);
    setIsFiltersBarVisible(!isFiltersBarVisible);
  };
  const handleGlobalSearchClick = (searchTerm: string) =>
    handleSearchClick ? handleSearchClick(searchTerm) : table.setGlobalFilter(searchTerm);
  const toggleColumnVisibilityControls = () => setIsColumnVisibilityControlsVisible(!isColumnVisibilityControlsVisible);

  const handleClearAllFiltersClick = () => {
    const clearedFilters =
      tableState?.columnFilters?.reduce((clearedColumnFilters, currentColumnFilter) => {
        if (Array.isArray(currentColumnFilter.value)) {
          return clearedColumnFilters;
        }
        clearedColumnFilters.push({ ...currentColumnFilter, value: false });
        return clearedColumnFilters;
      }, [] as ColumnFilter[]) ?? [];
    table.setColumnFilters(clearedFilters);
  };

  const handlePageChange = (newPageIndex: number) => table.setPageIndex(newPageIndex);

  const handlePageSizeChange = (newPageSize: number) => {
    table.setPagination({ pageIndex: 0, pageSize: newPageSize });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id && over?.id) {
      handlers.handleColumnOrderChange(String(active.id), String(over.id));
    }
  };

  if (isLoading || !meta.isInitialized) {
    return <TableLoading />;
  }

  return (
    <div className={clsx('flex w-full flex-col px-4 sm:px-6 lg:px-8', className)}>
      <ActionsBar
        actions={actions}
        activeFiltersCount={
          table.getState().columnFilters.filter((f) => (Array.isArray(f.value) ? f.value.length > 0 : f.value)).length
        }
        enableColumnFilters={enableColumnFilters}
        entityName={entityName}
        handleGlobalSearchClick={enableGlobalSearch ? handleGlobalSearchClick : undefined}
        isColumnVisibilityControlsVisible={enableColumnVisibility ? isColumnVisibilityControlsVisible : false}
        isFiltersBarVisible={isFiltersBarVisible}
        renderCustomElements={renderCustomElements?.(tableState?.isGroupByParent ?? false, handlers.setIsGroupByParent)}
        table={table}
        toggleColumnVisibilityControls={enableColumnVisibility ? toggleColumnVisibilityControls : undefined}
        toggleFiltersBarVisibility={enableColumnFilters ? toggleFiltersBarVisibility : undefined}
      />

      <div ref={filterRef} className={cn(!isFiltersBarVisible && 'hidden')}>
        <Show when={hasFiltersBarMounted && enableColumnFilters}>
          <FiltersBar
            entityName={entityName}
            table={table}
            wrapperClassName={filtersBarWrapperClassName}
            onClearAllFilters={handleClearAllFiltersClick}
            onHide={toggleFiltersBarVisibility}
          />
        </Show>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div
          ref={tableContainerRef}
          className={'border-info-200 relative mt-8 flex flex-col overflow-x-hidden rounded-lg border'}
          style={{
            width: '100%',
            height: 'calc(100vh - 100px)',
            boxSizing: 'border-box',
          }}
        >
          <div className="h-full min-w-0 overflow-x-auto overflow-y-auto">
            <div className={'inline-block h-full min-w-full align-middle'}>
              <div className="flex flex-col shadow-sm">
                <table
                  data-testid="data-table"
                  className="w-full"
                  style={{
                    boxSizing: 'border-box',
                  }}
                >
                  <TableHeader tableHeaderGroups={table.getHeaderGroups()} />

                  <Show when={isEmpty}>
                    <tbody className="h-full">
                      <tr className="h-full">
                        <td colSpan={meta.columnsCount} className="h-full">
                          <TableEmptyState entityName={entityName} action={meta.createEntityAction} />
                        </td>
                      </tr>
                    </tbody>
                  </Show>

                  <Show when={!isEmpty}>
                    <TableBody rows={table.getRowModel().rows} />
                  </Show>
                </table>
              </div>
            </div>
          </div>

          <Show when={enablePagination}>
            <TableFooter
              totalResults={totalResults ?? table.getFilteredRowModel().rows.length}
              totalResultsForDisplay={meta.totalResultsForDisplay}
              pageSize={tableState?.pagination?.pageSize ?? pageSize}
              pageIndex={tableState?.pagination?.pageIndex ?? 0}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </Show>
        </div>
      </DndContext>
    </div>
  );
}
